/*
  Follow us: the carousel and the post lightbox.

  Movement rules, matching the reference:
  - Opens framed, half an image showing at each edge.
  - The first click steps out of that frame by one image, landing on a whole
    image boundary.
  - Every click after moves by however many images fit on screen.
  - Past the end it runs back to the framed start rather than dead ending.

  On desktop the track is also sized to a whole number of images so the row
  clips on a boundary. That is not applied on narrow screens: the images are
  sized to nearly fill the width there, so rounding down to whole images can
  drop the count to one and leave the row stranded in the middle.

  Scroll snapping is off, since mandatory snap positions would pull the
  framing offset back to a track boundary.
*/
if (!customElements.get('follow-us')) {
  const TOLERANCE = 2;
  const DESKTOP = '(min-width: 990px)';

  class FollowUs extends HTMLElement {
    connectedCallback() {
      this.setupCarousel();
      this.setupLightbox();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.onResize);
      document.body.classList.remove('lightbox-open');
    }

    /* Carousel ------------------------------------------------------------ */

    setupCarousel() {
      this.track = this.querySelector('[data-track]');
      if (!this.track || !this.track.children.length) return;

      this.items = Array.from(this.track.children);
      this.prevButton = this.querySelector('[data-slide-prev]');
      this.nextButton = this.querySelector('[data-slide-next]');

      this.onResize = this.onResize.bind(this);

      if (this.prevButton) this.prevButton.addEventListener('click', () => this.slide(-1));
      if (this.nextButton) this.nextButton.addEventListener('click', () => this.slide(1));

      window.addEventListener('resize', this.onResize);

      // Wait a frame so the images have been laid out before measuring.
      requestAnimationFrame(() => {
        this.measure();
        this.track.scrollLeft = this.home;
      });
    }

    /*
      Only ever called on load and on resize. Measuring resets and reapplies
      the track width, which shifts scrollLeft, so doing it inside a click
      would move the row out from under the position being read.
    */
    measure() {
      // Items hidden by the mobile cap are out of the grid entirely, so the
      // first two visible ones give the true pitch.
      const visible = this.items.filter((item) => item.offsetParent !== null);
      if (!visible.length) return;

      const width = visible[0].offsetWidth;
      this.pitch = visible[1] ? visible[1].offsetLeft - visible[0].offsetLeft : width;
      if (!this.pitch) this.pitch = 1;

      const gap = this.pitch - width;

      // Release the constraint first, so the available width can be read.
      this.style.setProperty('--fu-track-w', 'none');
      const available = this.track.clientWidth;
      const fit = (available + gap) / this.pitch;

      if (window.matchMedia(DESKTOP).matches) {
        // Whole images only, and the track is sized to exactly that many so
        // there is no sliver of the next image at either edge.
        this.perView = Math.max(1, Math.floor(fit));
        this.style.setProperty('--fu-track-w', `${this.perView * this.pitch - gap}px`);
      } else {
        // Full width, and round rather than floor: the images are sized to
        // very nearly fill, so a few pixels short should still count.
        this.perView = Math.max(1, Math.round(fit));
      }

      // Half an image hidden on the left leaves half showing at each edge.
      this.home = this.pitch / 2;
      this.max = this.track.scrollWidth - this.track.clientWidth;

      const scrollable = this.max > TOLERANCE;
      if (this.prevButton) this.prevButton.hidden = !scrollable;
      if (this.nextButton) this.nextButton.hidden = !scrollable;
    }

    slide(direction) {
      if (!this.pitch) return;

      const current = this.track.scrollLeft;
      const atHome = Math.abs(current - this.home) < TOLERANCE;
      const atEnd = current >= this.max - TOLERANCE;
      let target;

      if (direction > 0) {
        if (atEnd) {
          // Past the end: run back to the framed start.
          target = this.home;
        } else if (atHome) {
          // Step out of the frame by one image, onto a clean boundary.
          target = this.pitch;
        } else {
          const index = Math.round(current / this.pitch) + this.perView;
          target = Math.min(index * this.pitch, this.max);
        }
      } else if (atHome) {
        target = this.max;
      } else {
        const index = Math.round(current / this.pitch) - this.perView;
        target = index <= 0 ? this.home : index * this.pitch;
      }

      this.track.scrollTo({ left: target, behavior: 'smooth' });
    }

    onResize() {
      this.measure();
    }

    /* Lightbox ------------------------------------------------------------ */

    setupLightbox() {
      this.lightbox = this.querySelector('[data-lightbox]');
      if (!this.lightbox) return;

      this.panels = Array.from(this.lightbox.querySelectorAll('[data-panel]'));
      if (!this.panels.length) return;

      this.index = 0;
      this.onKeydown = this.onKeydown.bind(this);
      this.addEventListener('click', this.onClick.bind(this));
    }

    onClick(event) {
      const opener = event.target.closest('[data-open]');
      if (opener) {
        event.preventDefault();
        this.opener = opener;
        this.open(Number(opener.dataset.open));
        return;
      }

      if (!this.lightbox.hasAttribute('open')) return;

      if (event.target.closest('[data-close]')) {
        this.close();
      } else if (event.target.closest('[data-prev]')) {
        this.go(this.index - 1);
      } else if (event.target.closest('[data-next]')) {
        this.go(this.index + 1);
      }
    }

    open(index) {
      this.go(index);
      this.lightbox.setAttribute('open', '');
      document.body.classList.add('lightbox-open');
      document.addEventListener('keydown', this.onKeydown);

      const close = this.lightbox.querySelector('[data-close-button]');
      if (close) close.focus();
    }

    go(index) {
      const total = this.panels.length;

      // Wraps, so the arrows never dead end.
      this.index = ((index % total) + total) % total;

      this.panels.forEach((panel, i) => {
        panel.toggleAttribute('hidden', i !== this.index);
      });

      const scroller = this.panels[this.index].querySelector('[data-panel-scroll]');
      if (scroller) scroller.scrollTop = 0;
    }

    close() {
      this.lightbox.removeAttribute('open');
      document.body.classList.remove('lightbox-open');
      document.removeEventListener('keydown', this.onKeydown);

      // Send focus back to the thumbnail that opened it.
      if (this.opener) this.opener.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.close();
      if (event.key === 'ArrowLeft') this.go(this.index - 1);
      if (event.key === 'ArrowRight') this.go(this.index + 1);
    }
  }

  customElements.define('follow-us', FollowUs);
}
